/**
 * ScorecardOrganism
 *
 * Considered existing organisms (atomic-design Rule 3): no organism composes a
 * context-view read + runtime table/card toggle + decision-capture affordance —
 * rejected because none fit; this is a new composition root, not a fork.
 *
 * Composition root for the 'scorecard' context viewType (see types/contextView.d.ts).
 * Loads a ContextView by system name, executes its context (status + points
 * enrichment) rooted at the sphere passed by the caller — typically the orbit
 * a scorecard metaspace organism is scoped to — and renders the result as a
 * table or a card grid, toggled at runtime (defaulting to the view config's
 * `display`). This component renders exactly the row-set the server returns and
 * never issues a per-row permission check (per /standards/frontend/permissions.md).
 *
 * NOTE on the server-side guarantee: `executeContext` authorizes the *caller*
 * against the root sphere's org (membership at the root) and `maskSpherePropertiesByAccess`
 * masks per-property `access.read` fields — it does NOT exclude whole rows for
 * private spheres the caller lacks `view` on. So the row-set is gated by
 * org-membership-at-the-root plus per-property masking, not a per-row `view` gate.
 * Row-level private-sphere exclusion in the shared context traversal is a known
 * platform gap tracked as a security follow-up (see EAINT-716 Open Questions);
 * do not describe this component as relying on a per-row server view gate.
 *
 * When `orbitSphere` + `organizationId` are supplied, also renders the
 * Meeting Decisions capture affordance for this scorecard's orbit (optional —
 * callers that only need the read-only rollup omit these props).
 */
import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useContextView } from '../../../hooks/useContextView';
import { useContextData } from '../../../hooks/useContextData';
import ScorecardTableMolecule from '../../molecules/ScorecardTable/ScorecardTableMolecule';
import ScorecardCardsMolecule from '../../molecules/ScorecardCards/ScorecardCardsMolecule';
import MeetingDecisionsMolecule from '../../molecules/MeetingDecisions/MeetingDecisionsMolecule';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import type { ScorecardViewConfig } from 'types/contextView';
import type { Sphere } from 'types/global';
import './ScorecardOrganism.scss';

const DEFAULT_SCORECARD_CONFIG: ScorecardViewConfig = {
  type: 'scorecard',
  surfaceColumns: { showStatus: true, showVelocity: true, showGravity: true },
  metricColumns: [],
  defaultSort: { column: 'name', direction: 'asc' },
  display: 'table',
};

export interface ScorecardOrganismProps {
  /** Context view system name — resolves the context + scorecard viewConfig */
  viewSystemName: string;
  /** Sphere to root the context execution at (the orbit/rotation this scorecard belongs to) */
  rootSphereId: string;
  /**
   * The orbit sphere, carrying its resolved `center_center` assignment. Required
   * only to enable the Meeting Decisions affordance — omit to render a read-only
   * scorecard.
   */
  orbitSphere?: Sphere;
  organizationId?: string;
  /** Current rotation instance, for decision-capture analytics only. */
  rotationSphereId?: string;
  className?: string;
}

const ScorecardOrganism: React.FC<ScorecardOrganismProps> = ({
  viewSystemName,
  rootSphereId,
  orbitSphere,
  organizationId,
  rotationSphereId,
  className = '',
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const { view, loading: viewLoading, error: viewError } = useContextView(apiClients, viewSystemName);

  const viewConfig: ScorecardViewConfig = useMemo(() => {
    if (view?.view_type === 'scorecard' && view.view_config) {
      return view.view_config as unknown as ScorecardViewConfig;
    }
    return DEFAULT_SCORECARD_CONFIG;
  }, [view]);

  const contextSystemName = view?.context_ref ?? '';

  const { data, loading: dataLoading, error: dataError } = useContextData(apiClients, {
    systemName: contextSystemName,
    rootSphereId,
    includeStatus: true,
    includePoints: true,
  });

  const spheres = useMemo(() => {
    if (!data) return [];
    return Object.values(data.spheresByColumn).flat();
  }, [data]);

  const [display, setDisplay] = useState<'table' | 'cards'>(viewConfig.display);

  useEffect(() => {
    setDisplay(viewConfig.display);
  }, [viewConfig.display]);

  if (!viewSystemName || !rootSphereId) {
    return (
      <div className="scorecard-organism__empty" data-testid="scorecard-organism-empty">
        {t('scorecard.organism.notConfigured')}
      </div>
    );
  }

  const loading = viewLoading || dataLoading;
  const error = viewError || dataError;

  return (
    <div className={`scorecard-organism ${className}`.trim()} data-testid="scorecard-organism">
      <div className="scorecard-organism__toolbar">
        <SegmentedControlAtom
          options={[
            { value: 'table', label: t('scorecard.organism.display.table'), icon: 'pi pi-table' },
            { value: 'cards', label: t('scorecard.organism.display.cards'), icon: 'pi pi-th-large' },
          ]}
          value={display}
          onChange={(value) => setDisplay(value as 'table' | 'cards')}
          mode="single"
          size="small"
          ariaLabel={t('scorecard.organism.display.ariaLabel')}
          data-testid="scorecard-organism-display-toggle"
        />
      </div>
      {display === 'cards' ? (
        <ScorecardCardsMolecule
          spheres={spheres}
          surfaceColumns={viewConfig.surfaceColumns}
          loading={loading}
          error={error}
        />
      ) : (
        <ScorecardTableMolecule
          spheres={spheres}
          surfaceColumns={viewConfig.surfaceColumns}
          defaultSort={viewConfig.defaultSort}
          loading={loading}
          error={error}
        />
      )}
      {orbitSphere && organizationId && (
        <MeetingDecisionsMolecule
          orbitSphere={orbitSphere}
          organizationId={organizationId}
          rotationSphereId={rotationSphereId}
          className="scorecard-organism__decisions"
        />
      )}
    </div>
  );
};

export default ScorecardOrganism;
